import { useCallback, useMemo, useState } from 'react';
import {
  AppointmentStatusRequired,
  AttributeType,
  ContactType,
  GetRoiTableViewReqAppointmentFilters,
  RoiSmsData,
  RoiType,
} from '@weave/schema-gen-ts/dist/schemas/analytics-reporting/analytics_gateway/analytics_gateway.pb';
import { LocationChip, UserCard } from '@frontend/analytics-shared';
import { useTranslation } from '@frontend/i18n';
import { useAppScopeStore } from '@frontend/scope';
import { useSearch } from '@frontend/weave-router';
import { theme } from '@frontend/theme';
import { Chip, SortingValue, Table, TableColumnConfig, Tabs } from '@frontend/design-system';
import { TableActions } from '../../../table-actions';
import { useFetchRoiPatients } from '../../hooks/use-fetch-roi-patients';
import { RoiBaseFilters, Sorting } from '../../types';

export interface MessagingRoiTableProps {
  attributeType: AttributeType;
  filters?: RoiBaseFilters;
  isDemoAccount?: boolean;
  middleColumnConfig?: TableColumnConfig<RoiSmsData>[];
  roiType: RoiType;
  trackingIdBase?: string;
}

type Tab = 'opportunities' | 'all' | 'appts_completed';

interface TablePanelProps extends MessagingRoiTableProps {
  tableInstanceId: string;
  tabType: Tab;
}

const TablePanel = ({
  attributeType,
  filters,
  isDemoAccount,
  middleColumnConfig = [],
  roiType,
  tableInstanceId,
  tabType,
  trackingIdBase = '',
}: TablePanelProps) => {
  const { t } = useTranslation('analytics');
  const { accessibleLocationData } = useAppScopeStore();
  const locationsData = accessibleLocationData;
  const search = useSearch({ strict: false }) ?? { view: 'leadsContacted' };

  const [sorting, setSorting] = useState<Sorting>({
    orderBy: 'desc',
    orderByField: 'entry_date',
  });

  const appointmentFilters = useMemo((): GetRoiTableViewReqAppointmentFilters => {
    const commonPayload = {
      // appointmentFilterFlag: true means filtered opportunities, else all leads (patients)
      appointmentFilterFlag: tabType === 'opportunities' || tabType === 'appts_completed',
    };

    if (attributeType === AttributeType.RAW_SMS) {
      return {
        ...commonPayload,
        appointmentScheduledFlag: false,
      };
    }

    if (attributeType === AttributeType.CONVERTED_PATIENTS) {
      return {
        ...commonPayload,
        appointmentStatus: {
          appointmentStatus: AppointmentStatusRequired.APPOINTMENT_COMPLETED,
          appointmentStatusEquality: search.view === 'revenueGenerated',
        },
      };
    }

    return {};
  }, [attributeType, search.view, tabType]);

  const { hasNextPage, hasPreviousPage, isLoading, pageConfig, patients, setPageConfig } = useFetchRoiPatients({
    payload: {
      attributeType,
      roiType,
      smsRequest: {
        ...sorting,
        appointmentFilters,
        endDate: filters?.endDate ?? '',
        locationId: filters?.locationIds,
        startDate: filters?.startDate ?? '',
      },
    },
    isDemoAccount,
  });

  const handleSorting = useCallback((sortValues: SortingValue<string>[]) => {
    const { id, value } = sortValues[0] || {};
    if (id && value) {
      setSorting({
        orderBy: value,
        orderByField: id,
      });
    }
  }, []);

  const columnConfig: TableColumnConfig<RoiSmsData>[] = useMemo(() => {
    return [
      {
        accessor: (patient) => patient,
        cellRenderer: ({ locationId, patientId, firstName = '', lastName = '', preferredName = '' }: RoiSmsData) => {
          return (
            <UserCard
              firstName={firstName}
              key={patientId}
              lastName={lastName}
              locationId={locationId}
              openProfileOnClick={!isDemoAccount}
              showOnlyName
              userId={isDemoAccount ? '' : patientId}
              preferredName={preferredName}
            />
          );
        },
        disableColumnFilter: true,
        disableSortBy: true,
        Header: t('Patient Name'),
        id: 'patient_name',
        sticky: 'left',
        width: 200,
      },
      {
        accessor: ({ locationId }) => locationId,
        cellRenderer: (value: string) => {
          return value ? <LocationChip locationName={locationsData?.[value]?.name || value} maxWidth={180} /> : '-';
        },
        disableSortBy: true,
        Header: t('Location'),
        id: 'location_id',
        omit: filters?.locationIds?.length === 1,
        width: 220,
      },
      {
        accessor: ({ contactType }) => contactType,
        cellRenderer: (value: ContactType) => {
          switch (value) {
            case ContactType.NEW_PATIENT:
              return <Chip variant='warn'>{t('New Patient')}</Chip>;

            case ContactType.EXISTING_PATIENT:
              return <Chip variant='primary'>{t('Existing Patient')}</Chip>;

            case ContactType.UNSHEDULED_PATIENT:
              return <Chip variant='indigo'>{t('Unscheduled')}</Chip>;

            default:
              return '-';
          }
        },
        disableSortBy: true,
        Header: t('Contact Type'),
        id: 'contact_type',
        width: 160,
      },
      ...middleColumnConfig,
      {
        accessor: ({ patientId, patientName, phoneNumber }) => ({
          patientId,
          patientName,
          phoneNumber,
        }),
        cellRenderer: ({ patientId, patientName, phoneNumber }) => {
          return (
            <TableActions
              isDemoAccount={isDemoAccount}
              personId={patientId}
              personName={patientName}
              phoneNumber={phoneNumber}
              trackingIdBase={`${trackingIdBase}-${tabType}`}
            />
          );
        },
        cellAlign: 'center',
        disableSortBy: true,
        Header: '',
        id: 'actions',
        sticky: 'right',
        width: 120,
      },
    ];
  }, [locationsData, middleColumnConfig]);

  return (
    <Table
      colConfig={columnConfig}
      data={patients}
      disableMultiSort
      hasResizeColumns
      hasResponsiveColWidths
      isLoading={isLoading}
      isPaginated
      manualPaginationConfig={{
        handleChange: (action: 'next' | 'prev') => {
          if (action === 'next') {
            setPageConfig({ ...pageConfig, pageNumber: pageConfig.pageNumber + 1 });
          } else {
            setPageConfig({ ...pageConfig, pageNumber: pageConfig.pageNumber - 1 });
          }
        },
        hasNext: hasNextPage,
        hasPrevious: hasPreviousPage,
        page: pageConfig.pageNumber,
        defaultRowsPerPage: pageConfig.pageSize,
        onNumRowsChange: (num) => {
          setPageConfig({ pageNumber: 1, pageSize: num });
        },
        rowsPerPageOptions: [10, 25, 50, 100],
      }}
      manualSortBy
      onSortChange={handleSorting}
      tableInstanceId={tableInstanceId}
    />
  );
};

export const MessagingRoiTable = (props: MessagingRoiTableProps) => {
  const { t } = useTranslation('analytics');
  const search = useSearch({ strict: false });
  const [activeTab, setActiveTab] = useState<Tab>(
    search.view === 'revenueGenerated' ? 'appts_completed' : 'opportunities'
  );

  return (
    <div>
      <Tabs initialTab={activeTab} onChange={(id: string) => setActiveTab(id as Tab)}>
        <Tabs.Bar css={{ marginBottom: theme.spacing(2) }}>
          {search.view === 'revenueGenerated'
            ? [
                <Tabs.Tab
                  id='appts_completed'
                  key='appts_completed'
                  trackingId={`${props.trackingIdBase}-appts_completed`}
                >
                  {t('Appointments Completed')}
                </Tabs.Tab>,
              ]
            : [
                <Tabs.Tab id='opportunities' key='opportunities' trackingId={`${props.trackingIdBase}-opportunities`}>
                  {t('Opportunities')}
                </Tabs.Tab>,
                <Tabs.Tab id='all' key='all' trackingId={`${props.trackingIdBase}-all`}>
                  {t('All')}
                </Tabs.Tab>,
              ]}
        </Tabs.Bar>

        {search.view === 'revenueGenerated' ? (
          <Tabs.Panel controller='appts_completed'>
            <TablePanel {...props} tableInstanceId='messaging-roi-appts-compeleted' tabType='appts_completed' />
          </Tabs.Panel>
        ) : (
          <>
            <Tabs.Panel controller='opportunities'>
              <TablePanel {...props} tableInstanceId='messaging-roi-opportunities-patients' tabType='opportunities' />
            </Tabs.Panel>
            <Tabs.Panel controller='all'>
              <TablePanel {...props} tableInstanceId='messaging-roi-all-patients' tabType='all' />
            </Tabs.Panel>
          </>
        )}
      </Tabs>
    </div>
  );
};
